/**
 * Camp Chef — mockup-aligned help-me-cook panel (modal or embedded).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTimes, FaRandom, FaMagic } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi2';
import { GiChefToque } from 'react-icons/gi';
import { communityRecipesService } from '../services/communityRecipesService';
import { proteinLabel } from '../campRecipesFilters';
import './camp-recipes-shell.css';

const CHEF_IDLE = "Tell me what you're craving or what's in your cooler!";

const CHEF_CONCOCTING = [
  'Checking the spice rack…',
  'Consulting the camp cookbook…',
  'Tasting for seasoning…',
  'Matching wild game to your pantry…',
  'Stirring the pot…',
];

const PLACEHOLDER =
  "e.g., I've got duck breasts and want something fancy… or just type 'venison' to find venison recipes";

function pickRandom(arr) {
  if (!arr?.length) return null;
  return arr[Math.floor(Math.random() * arr.length)];
}

export function HelpMeCookPanel({ onClose, onOpenRecipe }) {
  const [text, setText] = useState('');
  const [phase, setPhase] = useState('form');
  const [chefLine, setChefLine] = useState(CHEF_IDLE);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [surpriseRecipe, setSurpriseRecipe] = useState(null);
  const messageTimer = useRef(null);

  const clearMessageTimer = useCallback(() => {
    if (messageTimer.current) {
      clearInterval(messageTimer.current);
      messageTimer.current = null;
    }
  }, []);

  useEffect(() => () => clearMessageTimer(), [clearMessageTimer]);

  const startConcocting = () => {
    setPhase('loading');
    setError('');
    setResults(null);
    setSurpriseRecipe(null);
    let i = 0;
    setChefLine(CHEF_CONCOCTING[0]);
    clearMessageTimer();
    messageTimer.current = setInterval(() => {
      i += 1;
      setChefLine(CHEF_CONCOCTING[i % CHEF_CONCOCTING.length]);
    }, 800);
  };

  const stopConcocting = (message) => {
    clearMessageTimer();
    setChefLine(message);
    setPhase('results');
  };

  const resetToForm = () => {
    clearMessageTimer();
    setPhase('form');
    setChefLine(CHEF_IDLE);
    setResults(null);
    setSurpriseRecipe(null);
    setError('');
  };

  const findCommunityRecipes = async () => {
    const t = text.trim();
    if (t.length < 2) {
      setError('Describe what you have or what you are craving (at least 2 characters).');
      return;
    }
    startConcocting();
    try {
      const data = await communityRecipesService.helpMeCook(t);
      const rows = data?.results || [];
      if (rows.length === 0) {
        stopConcocting('No strong matches yet — try different wording or Surprise Me!');
        setResults([]);
      } else {
        stopConcocting('Found recipes from the community!');
        setResults(rows);
      }
    } catch (err) {
      clearMessageTimer();
      setPhase('form');
      setChefLine(CHEF_IDLE);
      const msg = err?.response?.data?.detail || err?.message || 'Request failed.';
      setError(typeof msg === 'string' ? msg : 'Request failed.');
    }
  };

  const surpriseMe = async () => {
    startConcocting();
    setText('');
    try {
      const data = await communityRecipesService.feed({ limit: 48, offset: 0 });
      const pool = data.recipes || [];
      const pick = pickRandom(pool);
      if (!pick) {
        stopConcocting('No recipes in the library yet — check back soon!');
        setSurpriseRecipe(null);
        return;
      }
      stopConcocting("Chef's surprise pick!");
      setSurpriseRecipe(pick);
    } catch {
      clearMessageTimer();
      setPhase('form');
      setChefLine(CHEF_IDLE);
      setError('Could not load a surprise recipe. Try again.');
    }
  };

  const openRecipe = (recipeId) => {
    if (onOpenRecipe) onOpenRecipe(recipeId);
  };

  const displayRecipes =
    surpriseRecipe != null
      ? [{ recipe: surpriseRecipe, match_score: null }]
      : results || [];

  return (
    <div className="camp-chef" data-testid="camp-chef-panel">
      <div className="camp-chef__header">
        {onClose ? (
          <button
            type="button"
            className="camp-chef__close"
            onClick={onClose}
            aria-label="Close Camp Chef"
          >
            <FaTimes aria-hidden="true" />
          </button>
        ) : null}
        <div className="camp-chef__header-main">
          <div
            className={`camp-chef__avatar${phase === 'loading' ? ' camp-chef__avatar--bounce' : ''}`}
            aria-hidden="true"
          >
            <GiChefToque className="camp-chef__avatar-icon" />
            {phase === 'loading' ? (
              <FaMagic className="camp-chef__avatar-sparkle" aria-hidden="true" />
            ) : null}
          </div>
          <div className="camp-chef__header-text">
            <h2 className="camp-chef__title">Camp Chef</h2>
            <p className="camp-chef__subtitle">{chefLine}</p>
          </div>
        </div>
      </div>

      <div className="camp-chef__body">
        {phase === 'form' ? (
          <>
            <div className="camp-chef__field">
              <label className="camp-chef__label" htmlFor="camp-chef-query">
                What sounds good?
              </label>
              <textarea
                id="camp-chef-query"
                className="camp-chef__textarea"
                rows={4}
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  setError('');
                }}
                placeholder={PLACEHOLDER}
              />
              {error ? (
                <p className="camp-chef__error" role="alert">
                  {error}
                </p>
              ) : null}
            </div>

            <div className="camp-chef__actions">
              <button
                type="button"
                className="camp-chef__btn camp-chef__btn--primary"
                disabled={!text.trim()}
                onClick={findCommunityRecipes}
              >
                <FaMagic aria-hidden="true" />
                Find community recipes
              </button>
              <button
                type="button"
                className="camp-chef__btn camp-chef__btn--ai"
                disabled
                aria-disabled="true"
                title="AI chef suggestions — coming soon"
              >
                <HiSparkles aria-hidden="true" />
                Ask AI Chef (Coming Soon)
              </button>
              <button
                type="button"
                className="camp-chef__btn camp-chef__btn--ghost"
                onClick={surpriseMe}
              >
                <FaRandom aria-hidden="true" />
                Surprise Me!
              </button>
            </div>
          </>
        ) : null}

        {phase === 'loading' ? (
          <div className="camp-chef__concoct" aria-live="polite">
            <div className="camp-chef__pot" aria-hidden="true">
              <div className="camp-chef__pot-body">
                <span className="camp-chef__bubble camp-chef__bubble--1" />
                <span className="camp-chef__bubble camp-chef__bubble--2" />
                <span className="camp-chef__bubble camp-chef__bubble--3" />
              </div>
              <div className="camp-chef__steam">
                <span />
                <span />
                <span />
              </div>
            </div>
            <p className="camp-chef__concoct-label">Concocting…</p>
          </div>
        ) : null}

        {phase === 'results' ? (
          <div className="camp-chef__results">
            {displayRecipes.length === 0 ? (
              <p className="camp-chef__results-empty">
                No matches yet. Try different wording or tap Surprise Me!
              </p>
            ) : (
              <ul className="camp-chef__result-list">
                {displayRecipes.map((row) => {
                  const r = row.recipe;
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        className="camp-chef__result-card"
                        onClick={() => openRecipe(r.id)}
                      >
                        {r.thumbnail_url ? (
                          <div className="camp-chef__result-media">
                            <img src={r.thumbnail_url} alt="" />
                            <div className="camp-chef__result-overlay" aria-hidden="true" />
                            <div className="camp-chef__result-caption">
                              {r.main_protein ? (
                                <span className="camp-chef__result-chip">
                                  {proteinLabel(r.main_protein)}
                                </span>
                              ) : null}
                              <h3>{r.title}</h3>
                            </div>
                          </div>
                        ) : (
                          <div className="camp-chef__result-body">
                            <h3>{r.title}</h3>
                            {r.description ? <p>{r.description}</p> : null}
                          </div>
                        )}
                        {row.match_score != null ? (
                          <span className="camp-chef__match">
                            Match score {row.match_score}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="camp-chef__results-actions">
              <button type="button" className="camp-chef__btn camp-chef__btn--ghost" onClick={resetToForm}>
                Try again
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
